'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, ArrowRight, CheckCircle, Building2, Palette, Music2, DollarSign, Shield, Mic2, Users, Zap, Star } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const STEPS = [
  { id: 1, label: 'Business Info', icon: Building2 },
  { id: 2, label: 'Branding', icon: Palette },
  { id: 3, label: 'Services', icon: Music2 },
  { id: 4, label: 'Pricing', icon: DollarSign },
  { id: 5, label: 'Portal Access', icon: Shield },
];

const SERVICES = [
  { id: 'dj', label: 'Mobile DJ', icon: Music2 },
  { id: 'karaoke', label: 'Karaoke Host', icon: Mic2 },
  { id: 'bingo', label: 'Bingo Night', icon: Star },
  { id: 'trivia', label: 'Trivia Night', icon: Zap },
  { id: 'corporate', label: 'Corporate Events', icon: Building2 },
  { id: 'weddings', label: 'Weddings', icon: Star },
  { id: 'birthday', label: 'Birthday Parties', icon: Users },
  { id: 'bar', label: 'Bar & Nightclub', icon: Zap },
];

const NEON_COLORS = [
  { label: 'Gold', value: '#FFD700' },
  { label: 'Cyan', value: '#00FFFF' },
  { label: 'Purple', value: '#BF00FF' },
  { label: 'Pink', value: '#FF006E' },
  { label: 'Green', value: '#00FF88' },
  { label: 'Orange', value: '#FF6B00' },
];

export default function WhiteLabelOnboarding() {
  const [step, setStep] = useState(1);
  const [launched, setLaunched] = useState(false);

  // Step 1
  const [bizInfo, setBizInfo] = useState({ companyName: '', ownerName: '', phone: '', email: '', city: '', state: '' });
  // Step 2
  const [primaryColor, setPrimaryColor] = useState('#FFD700');
  const [darkMode, setDarkMode] = useState(true);
  // Step 3
  const [selectedServices, setSelectedServices] = useState<string[]>(['dj']);
  // Step 4
  const [pricing, setPricing] = useState({ baseRate: '150', weddingRate: '200', depositPct: '25', cancelDays: '30' });
  // Step 5
  const [adminPass, setAdminPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  const toggleService = (id: string) => {
    setSelectedServices(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const canNext = () => {
    if (step === 1) return bizInfo.companyName && bizInfo.ownerName && bizInfo.email;
    if (step === 3) return selectedServices.length > 0;
    if (step === 5) return adminPass.length >= 8 && adminPass === confirmPass;
    return true;
  };

  if (launched) {
    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <div className="w-24 h-24 rounded-full gold-gradient flex items-center justify-center mx-auto mb-6 glow-gold">
            <CheckCircle size={44} className="text-black" />
          </div>
          <h1 className="text-4xl font-extrabold gold-gradient-text mb-4">Platform Launched! 🎉</h1>
          <p className="text-muted-foreground text-lg mb-2">
            <span className="text-foreground font-semibold">{bizInfo.companyName || 'Your Business'}</span> is live.
          </p>
          <p className="text-muted-foreground mb-8">Your admin dashboard, client portal, and vendor portal are ready. Check your email for login details.</p>
          <div className="space-y-3">
            <Link href="/admin-dashboard" className="block w-full py-3 rounded-xl gold-gradient text-black font-bold hover:opacity-90 transition-all">
              Go to Admin Dashboard
            </Link>
            <Link href="/white-label/customize" className="block w-full py-3 rounded-xl border border-primary/40 text-primary font-semibold hover:bg-primary/10 transition-all">
              Customize Branding
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b neon-border-gold">
        <div className="max-w-screen-md mx-auto px-6 flex items-center justify-between h-16">
          <Link href="/white-label" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
              <Music2 size={13} className="text-black" />
            </div>
            <span className="font-bold text-sm gold-gradient-text">Setup Wizard</span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">Step {step} of {STEPS.length}</span>
        </div>
      </div>

      <div className="max-w-screen-md mx-auto px-6 py-10">
        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = s.id === step;
              const isDone = s.id < step;
              return (
                <div key={s.id} className="flex flex-col items-center gap-1.5 flex-1">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${isDone ? 'gold-gradient' : isActive ? 'border-2 border-primary bg-primary/10' : 'border border-border bg-muted'}`}
                  >
                    {isDone ? <CheckCircle size={16} className="text-black" /> : <Icon size={15} className={isActive ? 'text-primary' : 'text-muted-foreground'} />}
                  </div>
                  <span className={`text-[10px] font-semibold hidden sm:block ${isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full gold-gradient rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Step Content */}
        <div className="card-glass rounded-2xl p-8 neon-border-gold min-h-[400px]">

          {/* STEP 1: Business Info */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-extrabold gold-gradient-text mb-2">Tell us about your business</h2>
              <p className="text-muted-foreground text-sm mb-8">This info will appear on your public site, contracts, and automated emails.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { key: 'companyName', label: 'Company Name *', placeholder: 'e.g. Platinum Sound Events' },
                  { key: 'ownerName', label: 'Owner Name *', placeholder: 'e.g. Marcus Thompson' },
                  { key: 'phone', label: 'Phone Number', placeholder: '(555) 000-0000' },
                  { key: 'email', label: 'Business Email *', placeholder: 'dj@yourbusiness.com' },
                  { key: 'city', label: 'City', placeholder: 'e.g. Atlanta' },
                  { key: 'state', label: 'State', placeholder: 'e.g. GA' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">{field.label}</label>
                    <input
                      type="text"
                      value={bizInfo[field.key as keyof typeof bizInfo]}
                      onChange={e => setBizInfo(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Branding */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-extrabold gold-gradient-text mb-2">Set your brand colors</h2>
              <p className="text-muted-foreground text-sm mb-8">Choose your primary neon color and display mode. You can change these anytime.</p>
              <div className="space-y-8">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Primary Neon Color</label>
                  <div className="flex flex-wrap gap-4">
                    {NEON_COLORS.map(c => (
                      <button key={c.value} onClick={() => setPrimaryColor(c.value)} className="flex flex-col items-center gap-2">
                        <div
                          className="w-12 h-12 rounded-xl transition-all"
                          style={{
                            background: c.value,
                            boxShadow: primaryColor === c.value ? `0 0 20px ${c.value}80` : 'none',
                            border: primaryColor === c.value ? `3px solid white` : '3px solid transparent',
                            transform: primaryColor === c.value ? 'scale(1.2)' : 'scale(1)',
                          }}
                        />
                        <span className="text-xs text-muted-foreground">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Display Mode</label>
                  <div className="flex gap-4">
                    {[{ label: 'Dark Mode', value: true }, { label: 'Light Mode', value: false }].map(opt => (
                      <button
                        key={opt.label}
                        onClick={() => setDarkMode(opt.value)}
                        className={`flex-1 py-3 rounded-xl border font-semibold text-sm transition-all ${darkMode === opt.value ? 'border-primary/60 text-primary bg-primary/10' : 'border-border text-muted-foreground'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground">You can upload your logo and set additional colors in the <span className="text-primary">Theme Customizer</span> after setup.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Services */}
          {step === 3 && (
            <div>
              <h2 className="text-2xl font-extrabold gold-gradient-text mb-2">What services do you offer?</h2>
              <p className="text-muted-foreground text-sm mb-8">Select all that apply. This configures your public site and booking options.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SERVICES.map(svc => {
                  const Icon = svc.icon;
                  const selected = selectedServices.includes(svc.id);
                  return (
                    <button
                      key={svc.id}
                      onClick={() => toggleService(svc.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${selected ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-border/80'}`}
                    >
                      <Icon size={22} />
                      <span className="text-xs font-semibold text-center leading-tight">{svc.label}</span>
                      {selected && <CheckCircle size={12} className="text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 4: Pricing */}
          {step === 4 && (
            <div>
              <h2 className="text-2xl font-extrabold gold-gradient-text mb-2">Set your base rates</h2>
              <p className="text-muted-foreground text-sm mb-8">These are used to auto-calculate retainer fees and contracts. You can adjust anytime.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { key: 'baseRate', label: 'Standard Event Rate ($/hr)', placeholder: '150' },
                  { key: 'weddingRate', label: 'Wedding Event Rate ($/hr)', placeholder: '200' },
                  { key: 'depositPct', label: 'Booking Deposit (%)', placeholder: '25' },
                  { key: 'cancelDays', label: 'Cancellation Notice (days)', placeholder: '30' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">{field.label}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        {field.key.includes('Pct') || field.key.includes('Days') ? '' : '$'}
                      </span>
                      <input
                        type="number"
                        value={pricing[field.key as keyof typeof pricing]}
                        onChange={e => setPricing(prev => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        className="w-full pl-7 pr-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
                <p className="text-xs text-muted-foreground">
                  <span className="text-primary font-semibold">Example:</span> A 4-hour standard event at ${pricing.baseRate}/hr = ${Number(pricing.baseRate) * 4} total. Booking deposit = ${Math.round(Number(pricing.baseRate) * 4 * Number(pricing.depositPct) / 100)}.
                </p>
              </div>
            </div>
          )}

          {/* STEP 5: Portal Access */}
          {step === 5 && (
            <div>
              <h2 className="text-2xl font-extrabold gold-gradient-text mb-2">Set your admin password</h2>
              <p className="text-muted-foreground text-sm mb-8">This secures your admin dashboard. Minimum 8 characters.</p>
              <div className="space-y-4 mb-8">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Admin Password *</label>
                  <input
                    type="password"
                    value={adminPass}
                    onChange={e => setAdminPass(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Confirm Password *</label>
                  <input
                    type="password"
                    value={confirmPass}
                    onChange={e => setConfirmPass(e.target.value)}
                    placeholder="Re-enter password"
                    className={`w-full px-4 py-2.5 rounded-lg bg-muted border text-foreground placeholder:text-muted-foreground/50 focus:outline-none text-sm ${confirmPass && adminPass !== confirmPass ? 'border-red-500/60' : 'border-border focus:border-primary'}`}
                  />
                  {confirmPass && adminPass !== confirmPass && (
                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Your portal URLs will be:</h3>
                {[
                  { label: 'Admin Dashboard', url: '/admin-dashboard', color: 'var(--neon-gold)' },
                  { label: 'Client Portal', url: '/client-workspace', color: 'var(--neon-cyan)' },
                  { label: 'Vendor Portal', url: '/vendor-portal', color: 'var(--neon-purple)' },
                ].map(p => (
                  <div key={p.label} className="flex items-center justify-between p-3 rounded-lg bg-muted border border-border">
                    <span className="text-sm font-semibold" style={{ color: p.color }}>{p.label}</span>
                    <span className="text-xs font-mono text-muted-foreground">{bizInfo.companyName ? `yourdomain.com${p.url}` : `yoursite.com${p.url}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-border/80 transition-all text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={15} /> Back
          </button>
          {step < STEPS.length ? (
            <button
              onClick={() => setStep(s => Math.min(STEPS.length, s + 1))}
              disabled={!canNext()}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl gold-gradient text-black font-bold hover:opacity-90 transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ArrowRight size={15} />
            </button>
          ) : (
            <button
              onClick={() => setLaunched(true)}
              disabled={!canNext()}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl gold-gradient text-black font-bold hover:opacity-90 transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Zap size={15} /> Launch My Platform
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
